import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, RefreshCcw, Search, X } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import ErrorBanner from '../components/ErrorBanner.jsx';
import { callsAPI } from '../services/api.js';
import { useEventStream } from '../hooks/useEventStream.js';
import {
  formatDateTime,
  formatDuration,
  formatPhone,
  statusBadge
} from '../lib/format.js';

// ⛔ THIS PAGE USED TO SHOW THE LAST 50 CALLS AND NOTHING ELSE.
//
// No pagination, no count, no search — and no hint that anything was missing. Prod
// placed 104 calls on 2026-08-11 alone, so "Calls" was under half a day of history
// presented as if it were the whole record. Alpha reported follow-up calls as
// invisible; on the campaign page they were not (that page loads 2,000 and groups
// them per contact), but HERE every call past the 50th was genuinely unreachable —
// there was no interaction that could ever reveal one.
//
// The API always supported this: GET /calls takes limit + offset and returns
// { data, total, limit, offset }. The page simply never asked.
//
// ⚠️ The filter box searches WHAT IS LOADED, not the server. That is a real limit and
// the page says so out loud rather than quietly returning "no matches" for a call
// that exists — the failure mode that makes someone trust an empty result.

const PAGE = 100; // SAFE_LIMIT in core; asking for more is silently clamped

export default function Calls() {
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  const readList = (res) =>
    Array.isArray(res) ? res : res?.calls || res?.data || [];

  // First page. Merges by id so a live call arriving over SSE doesn't discard the
  // pages already loaded — refreshing must never cost you your place.
  const load = useCallback(async (opts = {}) => {
    if (!opts.quiet) setLoading(true);
    setError('');
    try {
      const res = await callsAPI.list({ limit: PAGE });
      const list = readList(res);
      if (typeof res?.total === 'number') setTotal(res.total);
      setCalls((prev) => {
        if (!opts.quiet || prev.length === 0) return list;
        const seen = new Set(list.map((c) => c.id));
        return [...list, ...prev.filter((c) => !seen.has(c.id))];
      });
    } catch (err) {
      if (err.status === 404) {
        setError('Calls endpoint not yet available in the API.');
        setCalls([]);
      } else {
        setError(err.message || 'Failed to load calls.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const res = await callsAPI.list({ limit: PAGE, offset: calls.length });
      const list = readList(res);
      if (typeof res?.total === 'number') setTotal(res.total);
      setCalls((prev) => {
        const seen = new Set(prev.map((c) => c.id));
        return [...prev, ...list.filter((c) => !seen.has(c.id))];
      });
    } catch (err) {
      setError(err.message || 'Failed to load more calls.');
    } finally {
      setLoadingMore(false);
    }
  }, [calls.length]);

  useEffect(() => { load(); }, [load]);

  // Live events refresh the newest page without dropping what's already loaded.
  useEventStream({
    call_started: () => load({ quiet: true }),
    call_ended: () => load({ quiet: true })
  });

  const q = query.trim().toLowerCase();
  const qDigits = q.replace(/\D/g, '');
  const visible = useMemo(() => {
    if (!q) return calls;
    return calls.filter((c) => {
      const hay = `${c.fromNumber || ''} ${c.toNumber || ''} ${c.status || ''} ${c.kind || ''}`.toLowerCase();
      if (hay.includes(q)) return true;
      if (qDigits.length >= 3) {
        const nums = `${c.fromNumber || ''}${c.toNumber || ''}`.replace(/\D/g, '');
        if (nums.includes(qDigits)) return true;
      }
      return false;
    });
  }, [calls, q, qDigits]);

  const hasMore = total != null && calls.length < total;

  return (
    <div className="px-6 md:px-10 py-8 max-w-6xl">
      <PageHeader
        title="Calls"
        subtitle={
          total != null
            ? `${total.toLocaleString()} call${total === 1 ? '' : 's'} on your numbers`
            : 'Conversations from your phone numbers'
        }
        actions={
          <button type="button" onClick={() => load()} disabled={loading} className="btn-ghost">
            <RefreshCcw size={14} />
            Refresh
          </button>
        }
      />

      {error && (
        <div className="mb-6">
          <ErrorBanner message={error} />
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by number, status or kind"
            className="field-input w-full py-1.5 pl-9 pr-8 text-sm"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-300"
              aria-label="Clear filter"
            >
              <X size={14} />
            </button>
          )}
        </div>
        {/* Said plainly: the filter only sees loaded rows. A quiet "no matches" for a
            call that exists is worse than admitting the boundary. */}
        <p className="text-xs text-stone-500">
          {q ? `${visible.length} of ${calls.length} loaded` : `${calls.length} loaded`}
          {total != null && calls.length < total && (
            <> · {(total - calls.length).toLocaleString()} older not loaded{q ? ' — load more to search them' : ''}</>
          )}
        </p>
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-stone-500 border-b border-stone-800">
                <th className="px-4 py-3 font-medium">Started</th>
                <th className="px-4 py-3 font-medium">Kind</th>
                <th className="px-4 py-3 font-medium">From</th>
                <th className="px-4 py-3 font-medium">To</th>
                <th className="px-4 py-3 font-medium">Duration</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium" aria-label="Open" />
              </tr>
            </thead>
            <tbody>
              {loading && calls.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-stone-500">
                    Loading calls…
                  </td>
                </tr>
              ) : calls.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-stone-500">
                    No calls yet. When your phone numbers receive calls,
                    they'll appear here.
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-stone-500">
                    Nothing matching “{query.trim()}” in the {calls.length} calls loaded.
                    {hasMore && ' Older calls have not been loaded yet — load more below.'}
                  </td>
                </tr>
              ) : (
                visible.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/calls/${c.id}`)}
                    className="border-b border-stone-900 hover:bg-stone-900/50 cursor-pointer transition"
                  >
                    <td className="px-4 py-3 text-stone-300 whitespace-nowrap">
                      {formatDateTime(c.startedAt)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {c.kind === 'voicemail' ? (
                        <span className="badge bg-amber-500/15 text-amber-300 border border-amber-500/30">voicemail</span>
                      ) : c.kind === 'forwarded' ? (
                        <span className="badge bg-sky-500/15 text-sky-300 border border-sky-500/30">forwarded</span>
                      ) : (
                        <span className="text-stone-500 text-xs">conversation</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-stone-300 whitespace-nowrap">
                      {formatPhone(c.fromNumber)}
                    </td>
                    <td className="px-4 py-3 text-stone-300 whitespace-nowrap">
                      {formatPhone(c.toNumber)}
                    </td>
                    <td className="px-4 py-3 text-stone-300 tabular-nums whitespace-nowrap">
                      {formatDuration(c.durationSeconds)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`badge ${statusBadge(c.status)}`}>
                        {c.status || 'unknown'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-stone-500">
                      <ChevronRight size={16} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {hasMore && (
          <div className="border-t border-stone-900 px-4 py-3 text-center">
            <button type="button" onClick={loadMore} disabled={loadingMore} className="btn-ghost">
              {loadingMore
                ? 'Loading…'
                : `Load ${Math.min(PAGE, total - calls.length)} more (${(total - calls.length).toLocaleString()} older)`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
